import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../common/Button"
import LawFirmSearchOptionLabel from "../../../common/LawFirmSearchOptionLabel"
import PannelBody from "../../../fragments/PannelBody"
import { assignCaseAttorney, createLawFirm, generalAsyncSearch } from "../../../../lib/fetchApi"
import { formatLawFirmSearchLabel } from "../../../../lib/lawFirmSearchOptions"
import { notificationActions } from "../../../../store/notifications"
import { caseActions } from "../../../../store/case"
import inputClasses from "../../../common/interactive/InputBox.module.css"

import classes from './Create.module.css'
import matchClasses from './CreateLawFirmForm.module.css'

const MATCH_SEARCH_MIN_CHARS = 2
const MATCH_SEARCH_DEBOUNCE_MS = 400
const MATCH_LIST_LIMIT = 25

const CreateLawFirmForm = ({closeModal}) => {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [firmName, setFirmName] = useState('')
    const [isNameFocused, setIsNameFocused] = useState(false)
    const [matches, setMatches] = useState([])
    const [isSearchingMatches, setIsSearchingMatches] = useState(false)
    const [pendingMatch, setPendingMatch] = useState(null)

    const isSubmittingRef = useRef(false)
    const abortControllerRef = useRef(null)
    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)

    useEffect(() => {
        const trimmed = firmName.trim()
        if (trimmed.length < MATCH_SEARCH_MIN_CHARS) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
                abortControllerRef.current = null
            }
            setMatches([])
            setIsSearchingMatches(false)
            return
        }

        setMatches([])
        setIsSearchingMatches(true)

        const controller = new AbortController()
        abortControllerRef.current = controller

        const timer = setTimeout(async () => {
            try {
                const results = await generalAsyncSearch(
                    userToken,
                    'law-firms',
                    trimmed,
                    controller.signal
                )
                if (controller.signal.aborted) {
                    return
                }
                const list = Array.isArray(results) ? results.slice(0, MATCH_LIST_LIMIT) : []
                setMatches(list)
            } catch {
                if (controller.signal.aborted) {
                    return
                }
                setMatches([])
            } finally {
                if (!controller.signal.aborted) {
                    setIsSearchingMatches(false)
                }
            }
        }, MATCH_SEARCH_DEBOUNCE_MS)

        return () => {
            clearTimeout(timer)
            controller.abort()
        }
    }, [firmName, userToken])

    const assignFirmToCase = async (lawFirmId) => {
        const bindToCase = await assignCaseAttorney(userToken, activeCase.case.id, {
            law_firm_id: lawFirmId,
            location_id: null,
            attorney_id: null
        })

        if (bindToCase && bindToCase.errorMessage) {
            dispatch(notificationActions.setError(bindToCase))
            return false
        }

        const attorney = bindToCase?.id ? bindToCase : bindToCase?.data
        if (attorney?.id) {
            dispatch(caseActions.insertAttorneyInActiveCase(attorney))
        }

        closeModal()
        return true
    }

    const createAndAssign = async (name) => {
        const lawFirm = await createLawFirm(userToken, { name })

        if (lawFirm && lawFirm.errorMessage) {
            dispatch(notificationActions.setError(lawFirm))
            return false
        }

        return assignFirmToCase(lawFirm.id)
    }

    const runWithSubmitGuard = async (fn) => {
        if (isSubmittingRef.current) {
            return
        }
        isSubmittingRef.current = true
        setIsLoading(true)
        try {
            await fn()
        } finally {
            isSubmittingRef.current = false
            setIsLoading(false)
        }
    }

    const selectMatchHandler = (lawFirm) => {
        setPendingMatch(lawFirm)
    }

    const cancelMatchConfirmHandler = () => {
        if (isLoading) {
            return
        }
        setPendingMatch(null)
    }

    const confirmMatchAssignHandler = () => {
        if (!pendingMatch) {
            return
        }
        const firmId = pendingMatch.id
        setPendingMatch(null)
        runWithSubmitGuard(() => assignFirmToCase(firmId))
    }

    const createLawFirmHandler = (event) => {
        event.preventDefault()
        const name = firmName.trim()
        if (!name) {
            return
        }
        runWithSubmitGuard(() => createAndAssign(name))
    }

    const createAnywayHandler = (event) => {
        event.preventDefault()
        const name = firmName.trim()
        if (!name) {
            return
        }
        runWithSubmitGuard(() => createAndAssign(name))
    }

    const preventEnterSubmit = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
        }
    }

    const blockFormSubmit = (event) => {
        event.preventDefault()
    }

    const hasMatches = matches.length > 0
    const showMatchSection = firmName.trim().length >= MATCH_SEARCH_MIN_CHARS
    const nameLabelFloated = isNameFocused || firmName !== ''

    return <form onSubmit={blockFormSubmit} className={matchClasses.form_shell}>
        <PannelBody padding='large'>
            <div className={matchClasses.warning}>
                You are about to create a new Unverified Law Firm. If a matching firm already exists
                (Verified or Unverified), select it below to assign that firm instead of creating a duplicate.
            </div>

            <div className="row">
                <div className={inputClasses.input_box}>
                    <div className={`${inputClasses.input_box_fields} ${nameLabelFloated ? inputClasses.focused : ''}`}>
                        <label htmlFor="law_firm_name" className={inputClasses.label}>
                            Law Firm Name<span className={'required'}>*</span>
                        </label>
                        <input
                            autoComplete="off"
                            className={inputClasses.input}
                            type="text"
                            name="law_firm_name"
                            id="law_firm_name"
                            required
                            value={firmName}
                            onFocus={() => setIsNameFocused(true)}
                            onBlur={() => setIsNameFocused(firmName !== '')}
                            onChange={(event) => setFirmName(event.target.value)}
                            onKeyDown={preventEnterSubmit}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            </div>

            {showMatchSection && (
                <div className={matchClasses.matches_section}>
                    <div className={matchClasses.matches_heading}>
                        Potential matches
                        {isSearchingMatches && <span className={matchClasses.matches_status}>Searching…</span>}
                        {!isSearchingMatches && hasMatches && (
                            <span className={matchClasses.matches_status}>{matches.length} found</span>
                        )}
                        {!isSearchingMatches && !hasMatches && (
                            <span className={matchClasses.matches_status}>No matches found</span>
                        )}
                    </div>
                    {hasMatches && (
                        <ul className={matchClasses.matches_list}>
                            {matches.map((firm) => (
                                <li key={firm.id} className={matchClasses.match_item}>
                                    <div className={matchClasses.match_label}>
                                        <LawFirmSearchOptionLabel
                                            label={formatLawFirmSearchLabel(firm)}
                                            code={firm.code}
                                            name={firm.name}
                                            verified={firm.verified}
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        btnStyle="btn_small"
                                        click={() => selectMatchHandler(firm)}
                                        isDisabled={isLoading || Boolean(pendingMatch)}
                                    >
                                        Select
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className={classes.form_footer}>
                {hasMatches ? (
                    <div className={classes.confirmation_modal_controls_stacked}>
                        <Button type='button' isLoading={isLoading} width={'full'} click={createAnywayHandler} btnStyle={'btn_light_grey'} isDisabled={isSearchingMatches || Boolean(pendingMatch)}>
                            Create New Anyway
                            <i className="ti ti-chevron-right"></i>
                        </Button>
                    </div>
                ) : (
                    <Button type='button' isLoading={isLoading} width={'full'} click={createLawFirmHandler} isDisabled={(showMatchSection && isSearchingMatches) || Boolean(pendingMatch)}>
                        Submit
                        <i className="ti ti-chevron-right"></i>
                    </Button>
                )}
            </div>
        </PannelBody>

        {pendingMatch && (
            <div className={classes.confirmation_modal}>
                <div className={classes.confirmation_modal_card}>
                    <div className={classes.confirmation_modal_title}>Confirm assignment</div>
                    <p className={classes.confirmation_modal_text}>
                        Assign this law firm to the case?
                    </p>
                    <div className={classes.confirmation_modal_controls}>
                        <Button
                            type="button"
                            btnStyle="btn_light_grey"
                            click={cancelMatchConfirmHandler}
                            isDisabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            click={confirmMatchAssignHandler}
                            isLoading={isLoading}
                        >
                            Assign
                        </Button>
                    </div>
                </div>
            </div>
        )}
    </form>
}

export default CreateLawFirmForm
